import React, { useState } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { AlertCircle, Archive } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ClassType } from "@/lib/supabase/types";
import { isClassPast } from "@/lib/supabase/db/classes";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ClassesTabProps {
  classes: ClassType[] | undefined;
  isLoading: boolean;
  error: unknown;
  onDeleteClass: (id: string) => void;
}

const ClassesTab: React.FC<ClassesTabProps> = ({ classes, isLoading, error, onDeleteClass }) => {
  const [classToDelete, setClassToDelete] = useState<string | null>(null);
  
  const upcomingClasses = classes?.filter(cls => !isClassPast(cls)) || [];
  const pastClasses = classes?.filter(cls => isClassPast(cls)) || [];
  
  const renderClassesTable = (classesList: ClassType[], showDeleteButton: boolean = true) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Title</TableHead>
          <TableHead>Day</TableHead>
          <TableHead>Date</TableHead>
          <TableHead>Time</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead>Price</TableHead>
          <TableHead>Capacity</TableHead>
          <TableHead>Available</TableHead>
          {showDeleteButton && <TableHead>Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {classesList.map((cls) => (
          <TableRow key={cls.id}>
            <TableCell>{cls.title}</TableCell>
            <TableCell>{cls.day}</TableCell>
            <TableCell>{cls.date ? format(parseISO(cls.date), 'MMM d, yyyy') : '-'}</TableCell>
            <TableCell>{cls.time}</TableCell>
            <TableCell>{cls.duration}</TableCell>
            <TableCell>{cls.price} AMD</TableCell>
            <TableCell>{cls.capacity}</TableCell>
            <TableCell>{cls.availableSpots}</TableCell>
            {showDeleteButton && (
              <TableCell>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setClassToDelete(cls.id)}
                >
                  Delete
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Classes</CardTitle>
        <CardDescription>Manage your scheduled classes</CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Classes</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "Failed to load classes."}
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="text-center py-6">Loading classes...</div>
        ) : classes && classes.length > 0 ? (
          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="upcoming">Upcoming Classes ({upcomingClasses.length})</TabsTrigger>
              <TabsTrigger value="history">
                <Archive className="h-4 w-4 mr-1" />
                Class History ({pastClasses.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="upcoming">
              {upcomingClasses.length > 0 ? (
                renderClassesTable(upcomingClasses)
              ) : (
                <div className="text-center py-6">No upcoming classes found.</div>
              )}
            </TabsContent>
            
            <TabsContent value="history">
              {pastClasses.length > 0 ? (
                renderClassesTable(pastClasses, false)
              ) : (
                <div className="text-center py-6">No class history found.</div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-6">No classes found.</div>
        )}
        
        <AlertDialog open={!!classToDelete} onOpenChange={(open) => !open && setClassToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the class. If there are any bookings for this class, 
                the deletion will fail.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (classToDelete) {
                    onDeleteClass(classToDelete);
                    setClassToDelete(null);
                  }
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default ClassesTab;
